// ── BCHNostr Referral System ──────────────────────────────────────
//
// Architecture:
//   - Referral links: bchnostr.com?ref=<full-pubkey-hex>
//   - On landing, ref pubkey stored in localStorage
//   - On first qualifying post (kind 1 with #bch tag), the new user
//     publishes a kind 30078 "referral confirmation" event with:
//       d-tag: bchnostr/referral
//       ref: <referrer-pubkey>
//       action: first-post
//   - Leaderboard queries these events to build the recruiter rankings
//
// A "confirmed referral" = someone landed via your link AND made at
// least one BCH-tagged post. Impossible to fake without being a real
// active user (bots don't post BCH content for free).

import { nip19 } from 'nostr-tools';

export const REFERRAL_DRTAG = 'bchnostr/referral';
export const REFERRAL_KIND = 30078;
export const REFERRAL_STORAGE_KEY = 'bchnostr:referrer';
export const STAMP_STORAGE_KEY = 'bchnostr:stamp-wif';

/** Read ?ref= and ?stamp= from URL and store in localStorage */
export function captureReferral(): void {
  try {
    const params = new URLSearchParams(window.location.search);

    // Capture ambassador referral
    const ref = params.get('ref');
    if (ref) {
      // Must look like a valid hex pubkey (64 chars) or npub
      if (ref.length === 64 && /^[0-9a-f]+$/i.test(ref)) {
        localStorage.setItem(REFERRAL_STORAGE_KEY, ref);
      } else if (ref.startsWith('npub1') && ref.length > 10) {
        // Normalize npub to hex so the referral tag always uses hex
        try {
          const { data } = nip19.decode(ref);
          if (typeof data === 'string' && data.length === 64) {
            localStorage.setItem(REFERRAL_STORAGE_KEY, data);
          } else {
            localStorage.setItem(REFERRAL_STORAGE_KEY, ref);
          }
        } catch {
          localStorage.setItem(REFERRAL_STORAGE_KEY, ref);
        }
      }
    }

    // Capture CashStamp WIF — a pre-funded BCH private key.
    // The stamp param is swept into the user's tip wallet after signup.
    // Valid WIF keys start with K, L (compressed) or 5 (uncompressed).
    const stamp = params.get('stamp');
    if (stamp && /^[KL5][1-9A-HJ-NP-Za-km-z]{50,51}$/.test(stamp)) {
      localStorage.setItem(STAMP_STORAGE_KEY, stamp);
    }

    // Clean URL params so the WIF isn't visible in the address bar
    if (ref || stamp) {
      const clean = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', clean);
    }
  } catch {}
}

/** Get the stored referrer pubkey (hex), if any */
export function getStoredReferrer(): string | null {
  try {
    return localStorage.getItem(REFERRAL_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Clear the stored referrer (after confirmation event is published) */
export function clearStoredReferrer(): void {
  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {}
}

/** Whether the user has already published their referral confirmation */
export function hasConfirmedReferral(): boolean {
  try {
    return localStorage.getItem('bchnostr:referral-confirmed') === '1';
  } catch {
    return false;
  }
}

export function markReferralConfirmed(): void {
  try {
    localStorage.setItem('bchnostr:referral-confirmed', '1');
  } catch {}
}

// ── CashStamp helpers ────────────────────────────────────────────

/** Get the stored stamp WIF, if any */
export function getStoredStamp(): string | null {
  try {
    return localStorage.getItem(STAMP_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Clear the stored stamp (after it's been swept) */
export function clearStoredStamp(): void {
  try {
    localStorage.removeItem(STAMP_STORAGE_KEY);
  } catch {}
}

/**
 * Whether a stamp has already been swept.
 * Uses a per-WIF marker so that a user who scans multiple different stamps
 * over time will correctly sweep each one. The old global flag is also
 * cleaned up if present.
 */
export function hasStampBeenSwept(wif?: string | null): boolean {
  try {
    // Per-stamp marker (new system)
    if (wif) {
      // Use a short hash of the WIF to avoid storing the full key
      const marker = 'bchnostr:stamp-swept:' + wif.slice(0, 8) + wif.slice(-4);
      if (localStorage.getItem(marker) === '1') return true;
    }
    return false;
  } catch {
    return false;
  }
}

export function markStampSwept(wif?: string | null): void {
  try {
    if (wif) {
      const marker = 'bchnostr:stamp-swept:' + wif.slice(0, 8) + wif.slice(-4);
      localStorage.setItem(marker, '1');
    }
    // Clean up old global flag if present
    localStorage.removeItem('bchnostr:stamp-swept');
  } catch {}
}

